import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { MMS_MAX_SIZE, MessagesHooks, SchemaSMSSharedEnums, SchemaSMSSharedModels } from '@frontend/api-messaging';
import { PersonHelpers } from '@frontend/api-person';
import { SMSDataV3 } from '@frontend/api-sms-data';
import { getUser, hasSchemaACL } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppFlagStore } from '@frontend/shared';
import { useTaskCitation } from '@frontend/task-center-scope';
import { ThreadBodyUtils } from '@frontend/thread-body';
import { sentry } from '@frontend/tracking';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { ContextMenuActions as DSContextMenuActions, ModalControlResponse, useAlert } from '@frontend/design-system';
import { HIDE_DELETE_MODAL } from '../constants';
import { LocalSendingStatus, SentMessage } from '../types';

type ContextMenuActions = (Omit<DSContextMenuActions[number], 'label'> & {
  label: string;
})[];

type Props = {
  message: SentMessage;
  modalControl: ModalControlResponse;
  groupId: string;
  isDeleted: boolean;
  onResend?: () => void;
  personData?: {
    personId: string;
    firstName: string;
    lastName: string;
    preferredName?: string;
  };
};

type BubbleActions = {
  globalActions: DSContextMenuActions;
  mediaActions: (mediaItem: Partial<SchemaSMSSharedModels.Media>) => DSContextMenuActions;
  messageActions: DSContextMenuActions;
  handleSmsDelete: () => void;
  retrySend: (mediaItem?: Partial<SchemaSMSSharedModels.Media>) => void;
};

export const useBubbleActions = ({
  message,
  modalControl,
  groupId,
  isDeleted,
  onResend,
  personData,
}: Props): BubbleActions => {
  const { t } = useTranslation('inbox');
  const alert = useAlert();
  const user = getUser();
  const { featureFlags } = useAppFlagStore();
  const { clearFiles } = MessagesHooks.useMediaUploader({
    maxFileSize: MMS_MAX_SIZE,
    onExceedMaxFiles: () => {
      alert.error({ message: t('You can only attach up to 10 images at a time.') });
    },
  });
  const smsId = message.id;

  const aclEnforcementEnabled = !!featureFlags[groupId]?.get('nwx:messaging-inbox-fe-acl-enforcement')?.value;
  const isTaskCenterAvailable = !!featureFlags[groupId]?.get('new-task-center-experience')?.value;
  const { setMessageCitation } = useTaskCitation();
  const disableDelete = aclEnforcementEnabled && !hasSchemaACL(groupId, Permission.SMS_INBOX_MESSAGE_DELETE);

  const { mutate: deleteSms } = SMSDataV3.Mutations.useDeleteSMSMutation();

  const handleSmsDelete = async () => {
    deleteSms(
      {
        smsId,
        locationId: groupId,
        userId: user?.userID ?? '',
        createdAt: message.createdAt,
        _otherOptions: {
          threadId: message.threadId ?? '',
          isAutomated: message.autogeneratedBy !== undefined ? !!message.autogeneratedBy : false,
        },
      },
      {
        onSuccess: () => {
          alert.success(t('Message deleted'));
        },
        onError: () => {
          alert.error(t('Error deleting message'));
        },
      }
    );
  };

  const copySms = () => {
    navigator.clipboard.writeText(message?.body ?? '');
    alert.success(t('Message Copied'));
  };

  const { isLoading, mutateAsync: resendMessage } = SMSDataV3.Mutations.useResendSMSMutation({
    options: {
      onSuccess: () => {
        onResend?.();
        clearFiles();
      },
      onError: () => {
        alert.error(t('Error resending message'));
      },
    },
    optimisticUpdate: true,
  });

  const hasRetry =
    !isLoading &&
    (message.status === SchemaSMSSharedEnums.Status.STATUS_ERROR ||
      message.status === SchemaSMSSharedEnums.Status.STATUS_NOT_SENT) &&
    !message.deletedAt;

  const retrySend = () => {
    resendMessage({
      smsId: message.id,
      threadId: message.threadId,
      locationId: message.locationId ?? '',
      isAutomated: message.autogeneratedBy !== undefined ? !!message.autogeneratedBy : false,
    });
  };

  const COPY_MSG_ACTION = {
    label: t('Copy Message'),
    Icon: () => <Icon name='copy' size={18} />,
    onClick: () => copySms(),
    trackingId: `${InboxPrefixes.Thread}-message-action-copy`,
  };
  const COPY_IMG_ACTION = (mediaItem: Partial<SchemaSMSSharedModels.Media>) => ({
    label: t('Copy Image'),
    Icon: () => <Icon name='copy' size={18} />,
    onClick: () => {
      try {
        ThreadBodyUtils.MediaInteractionUtils.copyImage(mediaItem.mediaId!, groupId);
        alert.success(t('Image Copied'));
      } catch (err) {
        alert.error(t('Error copying image'));
        sentry.error({
          error: err,
          topic: 'messages',
          severityLevel: 'error',
          addContext: {
            name: 'Message object & media item',
            context: {
              message,
              mediaItem,
              errMessage: 'Error copying image from message',
            },
          },
        });
      }
    },
    disabled: !mediaItem.mediaId,
    trackingId: `${InboxPrefixes.Thread}-message-action-copy-image`,
  });
  const DOWNLOAD_IMG_ACTION = (mediaItem: Partial<SchemaSMSSharedModels.Media>) => ({
    label: t('Download Image'),
    Icon: () => <Icon name='download' size={18} />,
    onClick: () => {
      try {
        ThreadBodyUtils.MediaInteractionUtils.downloadImage({
          mediaId: mediaItem.mediaId!,
          filename: mediaItem.filename ?? `${message.personPhone}-${t('image-download')}`,
          locationId: groupId,
        });
        alert.success(t('Image downloaded'));
      } catch (err) {
        alert.error(t('Error downloading image'));
        sentry.error({
          error: err,
          topic: 'messages',
          severityLevel: 'error',
          addContext: {
            name: 'Message object & media item',
            context: {
              message,
              mediaItem,
              errMessage: 'Error downloading image from message',
            },
          },
        });
      }
    },
    disabled: !mediaItem.mediaId,
    trackingId: `${InboxPrefixes.Thread}-message-action-download-image`,
  });
  const RETRY_MSG_ACTION = {
    label: t('Retry Send'),
    Icon: () => <Icon name='update' size={18} />,
    onClick: () => retrySend(),
    trackingId: `${InboxPrefixes.Thread}-message-action-retry`,
  };
  const DELETE_ACTION = {
    label: t('Delete from Thread'),
    Icon: () => <Icon name='trash' size={18} />,
    disabled: disableDelete,
    hoverLabel: disableDelete ? t('You do not have permission to delete messages.') : undefined,
    onClick: () => {
      const showDeleteModal = localStorage.getItem(HIDE_DELETE_MODAL) !== 'true';
      if (showDeleteModal) {
        modalControl.openModal();
      } else {
        handleSmsDelete();
      }
    },
    trackingId: `${InboxPrefixes.Thread}-message-action-delete`,
  };

  const TASK_CENTER_ACTION = {
    label: t('Create Task'),
    Icon: () => <Icon name='mark-done' size={18} />,
    onClick: () => {
      setMessageCitation({
        sourceId: message.id,
        source: {
          description: message.body,
          phone_no: message.personPhone,
          created_at: message.createdAt,
          user_id: user?.userID,
          patient_id: personData?.personId,
        },
        personName: PersonHelpers.getFullName({
          FirstName: personData?.firstName,
          LastName: personData?.lastName,
          PreferredName: personData?.preferredName,
        }),
        locationId: message.locationId ?? '',
      });
    },
    trackingId: 'task-center-message-action-create-task',
  };

  const getActions = (mediaItem?: Partial<SchemaSMSSharedModels.Media>) =>
    [
      !isDeleted && !mediaItem && COPY_MSG_ACTION,
      mediaItem && COPY_IMG_ACTION(mediaItem),
      mediaItem && DOWNLOAD_IMG_ACTION(mediaItem),
      hasRetry && RETRY_MSG_ACTION,
      message.localSendingStatus !== LocalSendingStatus.SENDING && !isDeleted && DELETE_ACTION,
    ].filter(Boolean);

  const globalActions: ContextMenuActions = [
    !isDeleted && COPY_MSG_ACTION,
    isTaskCenterAvailable && TASK_CENTER_ACTION,
    message.localSendingStatus !== LocalSendingStatus.SENDING && !isDeleted && DELETE_ACTION,
  ].filter(Boolean);

  return {
    globalActions,
    mediaActions: getActions,
    messageActions: getActions(),
    handleSmsDelete,
    retrySend,
  };
};
